import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Loader2, X, Tag, Check } from "lucide-react";

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-md border border-spark-line overflow-hidden" data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold transition-colors ${value === v ? "bg-spark-teal text-white" : "bg-white text-spark-slate hover:text-spark-teal"}`}
        data-testid={`${testid}-${v}`}>{label}</button>
    ))}
  </div>
);

const DiscountForm = ({ job, onUpdated, onDone }) => {
  const [type, setType] = useState("pct");
  const [scope, setScope] = useState("labour");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const v = parseFloat(value);
    if (!v || v <= 0) { toast.error("Enter a discount amount"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/admin/jobs/${job.id}/discount`, { type, value: v, scope, reason });
      onUpdated(res.data);
      toast.success("Discount applied");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't apply discount");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="discount-form">
      <div className="flex flex-wrap gap-2">
        <Seg testid="discount-type" value={type} onChange={setType} options={[["pct", "%"], ["fixed", "$"]]} />
        <Seg testid="discount-scope" value={scope} onChange={setScope} options={[["labour", "Labour"], ["all", "Whole job"]]} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input type="number" min="0" step="0.5" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "pct" ? "10" : "20.00"} className="input-style text-sm" data-testid="discount-value" />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Label shown to customer (optional)" className="input-style text-sm col-span-2" data-testid="discount-reason" />
      </div>
      {job.discount && <p className="text-[11px] text-spark-slate">This replaces the current discount{job.discount.points_spent && !job.discount.points_refunded ? ` and refunds ${job.discount.points_spent} pts` : ""}.</p>}
      <button onClick={apply} disabled={busy} className="btn-primary w-full text-xs" data-testid="discount-apply-btn">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} Apply discount
      </button>
    </div>
  );
};

export const JobDiscountCard = ({ job, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const d = job.discount;

  const remove = async () => {
    if (!window.confirm(`Remove "${d.label}"?${d.points_spent && !d.points_refunded ? ` ${d.points_spent} points will go back to the customer.` : ""}`)) return;
    setBusy(true);
    try {
      const res = await api.delete(`/admin/jobs/${job.id}/discount`);
      onUpdated(res.data);
      toast.success("Discount removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove discount");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card p-5 sm:p-6 border-l-4 border-spark-yellow" data-testid="job-discount-card">
      <p className="label-mono text-spark-teal flex items-center gap-1.5 mb-3"><Tag className="size-4" /> Discount</p>
      {d ? (
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-sm min-w-0">
            <p className="font-bold text-spark-ink" data-testid="job-discount-label">{d.label}</p>
            <p className="text-xs text-spark-slate mt-0.5">
              {d.source === "admin" ? "Given by workshop" : null}
              {d.points_spent ? `${d.points_spent} pts redeemed${d.points_refunded ? " (refunded)" : ""}` : null}
              {d.points_spent && d.referral_code ? " · " : null}
              {d.referral_code && <span className="inline-flex items-center gap-1"><Gift className="size-3" /> Code {d.referral_code}{d.referrer_email && <> from <Link to={`/admin/customers/${encodeURIComponent(d.referrer_email)}`} className="underline">{d.referrer_email}</Link></>}</span>}
            </p>
          </div>
          <button onClick={remove} disabled={busy} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-spark-line text-xs uppercase tracking-wider font-bold text-spark-slate hover:text-red-600 hover:border-red-400" data-testid="remove-discount-btn">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />} Remove
          </button>
        </div>
      ) : null}
      {!d || showForm ? (
        <DiscountForm job={job} onUpdated={onUpdated} onDone={() => setShowForm(false)} />
      ) : (
        <button onClick={() => setShowForm(true)} className="text-xs uppercase tracking-wider font-bold text-spark-teal hover:underline" data-testid="replace-discount-btn">Replace with a different discount</button>
      )}
      {job.points_awarded != null && (
        <p className="text-xs text-emerald-700 mt-3" data-testid="job-points-awarded">+{job.points_awarded} points earned on payment{job.referral_rewarded_at ? " · referrer bonus paid" : ""}</p>
      )}
    </div>
  );
};
